import { z } from 'zod';

import { FetchLoggedInUserError } from '../errors/FetchLoggedInUserError';
import { NotAuthenticatedError } from '../errors/NotAuthenticatedError';
import { ResponseBodyValidationError } from '../errors/ResponseBodyValidationError';

import { httpStatusCode } from './httpStatusCode';
import { prefectureIds, type PrefectureId } from './prefecture';
import { qualificationStudent, type QualificationCode } from './qualification';
import { createFailureResult, createSuccessResult, Result } from './result';
import { bffUrl } from './url/apiUrl';
import { validation } from './validation';

import type { AppEnv } from './appEnv';
import type { HttpsUrl } from './url';

export type Sub = number;

export type PreferredUsername = string;

export type PictureUrl = HttpsUrl;

export type Birthdate = `${string}-${string}-${string}`;

export type Email = `${string}@${string}`;

export type LoginSessionToken = string;

// https://bff.nurse-senka.jp/users/authenticated の loggedInUserのレスポンス型
type FetchAuthenticatedLoggedInUser = {
  sub: Sub;
  pictureUrl: PictureUrl;
  pointBalance: number;
  preferredUsername?: PreferredUsername;
  birthdate?: Birthdate;
  prefectureId?: PrefectureId;
  email?: Email;
  qualificationCode?: QualificationCode[];
};

// https://bff.nurse-senka.jp/users/authenticated のレスポンス型
type FetchAuthenticatedUserSuccessResponse = {
  loggedInUser: FetchAuthenticatedLoggedInUser;
  loginSessionToken: LoginSessionToken;
  refreshedToken: boolean;
};

const prefectureIdValidationDefinition = prefectureIds.map((value) =>
  z.literal(value),
);

// 以下の通り過去のユーザーは現在使われていない 5 を持っている事があるので5も許容する
// 新規登録時や変更時は5は許容したくないので qualificationCodeList はそのままにしておく
// https://github.com/nurse-senka/nurse-senka/issues/782#issuecomment-1504853181
// eslint-disable-next-line no-magic-numbers
const qualificationCodeListValidationDefinition = [1, 2, 3, 4, 5, 6, 7].map(
  (value) => z.literal(value),
);

const fetchAuthenticatedUserSuccessResponseSchema = z.object({
  loggedInUser: z.object({
    // eslint-disable-next-line no-magic-numbers
    sub: z.number().min(1).max(Number.MAX_SAFE_INTEGER),
    // eslint-disable-next-line no-magic-numbers
    preferredUsername: z.string().min(1).optional(),
    pictureUrl: z.string().url(),
    // eslint-disable-next-line no-magic-numbers
    pointBalance: z.number().min(0).optional(),
    // eslint-disable-next-line no-magic-numbers
    birthdate: z.string().min(10).optional(),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    prefectureId: z.union(prefectureIdValidationDefinition).optional(),
    email: z.string().optional(),
    qualificationCode: z
      .array(
        z.union(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          qualificationCodeListValidationDefinition,
        ),
      )
      .nonempty()
      .optional(),
  }),
  // eslint-disable-next-line no-magic-numbers
  loginSessionToken: z.string().min(1),
  refreshedToken: z.boolean(),
});

const isFetchAuthenticatedUserSuccessResponse = (
  value: unknown,
): value is FetchAuthenticatedUserSuccessResponse =>
  validation(fetchAuthenticatedUserSuccessResponseSchema, value).isValidate;

// 元々のloggedInUserのレスポンス型から isStudent を追加して preferredUsername を必須にしている
export type LoggedInUser = FetchAuthenticatedLoggedInUser & {
  preferredUsername: string;
  isStudent: boolean;
};

export type FetchLoggedInUserResponse = {
  loggedInUser: LoggedInUser;
  loginSessionToken: LoginSessionToken;
  refreshedToken: boolean;
  xRequestId: string;
};

export type FetchLoggedInUser = (
  appEnv: AppEnv,
  loginSessionToken: LoginSessionToken,
  uniqueIdGenerator: () => string,
) => Promise<Result<FetchLoggedInUserResponse, NotAuthenticatedError>>;

type FetchAuthenticatedUserNotAuthenticatedErrorResponse = {
  type: 'UNAUTHENTICATED';
  title: 'User is not authenticated.';
  // eslint-disable-next-line no-magic-numbers
  status: 401;
};

const fetchAuthenticatedUserNotAuthenticatedErrorResponseSchema = z.object({
  type: z.literal('UNAUTHENTICATED'),
  title: z.literal('User is not authenticated.'),
  // eslint-disable-next-line no-magic-numbers
  status: z.literal(401),
});

const isFetchAuthenticatedUserNotAuthenticatedErrorResponse = (
  value: unknown,
): value is FetchAuthenticatedUserNotAuthenticatedErrorResponse =>
  validation(fetchAuthenticatedUserNotAuthenticatedErrorResponseSchema, value)
    .isValidate;

const isStudentUser = (
  fetchAuthenticatedLoggedInUser: FetchAuthenticatedLoggedInUser,
): boolean => {
  if (
    fetchAuthenticatedLoggedInUser.qualificationCode?.find(
      (value) => value === qualificationStudent.code,
    )
  ) {
    return true;
  }

  return false;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const fetchLoggedInUser: FetchLoggedInUser = async (
  appEnv,
  loginSessionToken,
  uniqueIdGenerator,
) => {
  const xRequestId = uniqueIdGenerator();

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Request-Id': uniqueIdGenerator(),
    },
    body: JSON.stringify({
      loginSessionToken,
    }),
  };

  const response = await fetch(
    `${bffUrl(appEnv)}/users/authenticated`,
    options,
  );

  if (response.status !== httpStatusCode.ok) {
    const responseBody = await response.json();
    if (isFetchAuthenticatedUserNotAuthenticatedErrorResponse(responseBody)) {
      return createFailureResult<NotAuthenticatedError>(
        new NotAuthenticatedError(xRequestId),
      );
    }

    throw new FetchLoggedInUserError(xRequestId);
  }

  const responseBody =
    (await response.json()) as FetchAuthenticatedUserSuccessResponse;

  if (isFetchAuthenticatedUserSuccessResponse(responseBody) === false) {
    const validationResult = validation(
      fetchAuthenticatedUserSuccessResponseSchema,
      responseBody,
    );
    if (!validationResult.isValidate && validationResult.invalidParams) {
      throw new ResponseBodyValidationError(xRequestId, {
        invalidParams: validationResult.invalidParams,
      });
    }
  }

  const isStudent = isStudentUser(responseBody.loggedInUser);

  const preferredUsername = responseBody.loggedInUser?.preferredUsername
    ? responseBody.loggedInUser?.preferredUsername
    : '未設定';

  const loggedInUser = {
    ...responseBody.loggedInUser,
    isStudent,
    preferredUsername,
  };

  return createSuccessResult<FetchLoggedInUserResponse>({
    loggedInUser,
    loginSessionToken: responseBody.loginSessionToken,
    refreshedToken: responseBody.refreshedToken,
    xRequestId,
  });
};
